import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { DeltaPill } from './RatingBadge';
import { TONE_BG, type Tone } from './tones';

/**
 * A number with a small caps label: PPG, FG%, W-L, streaks.
 *
 *   <StatTile label="PPG" value="11.4" delta={2} />
 *   <StatTile label="FG" value={48} unit="%" tone="sky" hint="Last 30 days" />
 */
export interface StatTileProps {
  label: string;
  value: ReactNode;
  unit?: string;
  delta?: number | null;
  hint?: string;
  tone?: Tone;
  size?: 'md' | 'lg';
  className?: string;
}

export function StatTile({ label, value, unit, delta, hint, tone = 'white', size = 'md', className }: StatTileProps) {
  return (
    <div
      className={clsx(
        'flex min-w-0 flex-col gap-1 rounded-tts border-(length:--tts-border) border-ink px-3 py-2.5 shadow-hard-sm',
        TONE_BG[tone],
        className,
      )}
    >
      <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      <span className="flex items-baseline gap-1">
        <span className={clsx('tts-display tabular-nums leading-none', size === 'lg' ? 'text-5xl' : 'text-4xl')}>
          {value}
        </span>
        {unit && <span className="font-display text-lg font-black italic">{unit}</span>}
      </span>
      {(delta || hint) && (
        <span className="flex items-center gap-2 text-xs font-semibold">
          {delta ? <DeltaPill delta={delta} /> : null}
          {hint && <span className="truncate">{hint}</span>}
        </span>
      )}
    </div>
  );
}
